import React, { useState } from 'react';
import { VonChatFilters } from '../types';
import { DateRangeFilter } from './DateRangeFilter';
import { ClientFilter } from './ClientFilter';
import { ModelSelector } from './ModelSelector';
import { NeuralSearchToggle } from './NeuralSearchToggle';

interface FilterPanelProps {
  filters: VonChatFilters;
  onFiltersChange: (filters: Partial<VonChatFilters>) => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  filters,
  onFiltersChange,
  isCollapsed = false,
  onToggleCollapse
}) => {
  const [activeSection, setActiveSection] = useState<string | null>('neural');

  const hasActiveFilters = () => {
    return !!(
      filters.dateRange ||
      (filters.selectedClients && filters.selectedClients.length > 0) ||
      filters.selectedModel ||
      filters.precisionSearchQuery
    );
  };

  const clearAllFilters = () => {
    onFiltersChange({
      dateRange: undefined,
      selectedClients: [],
      selectedModel: undefined,
      precisionSearchQuery: undefined,
      useNeuralSearch: true
    });
  };

  const getFilterSummary = () => {
    const parts = [];
    
    if (filters.dateRange) {
      if (filters.dateRange.preset && filters.dateRange.preset !== 'custom') {
        parts.push(`Last ${filters.dateRange.preset.replace('days', ' days')}`);
      } else {
        parts.push('Custom date range');
      }
    }
    
    if (filters.selectedClients && filters.selectedClients.length > 0) {
      parts.push(`${filters.selectedClients.length} client${filters.selectedClients.length !== 1 ? 's' : ''}`);
    }
    
    if (filters.selectedModel) {
      parts.push(`Model: ${filters.selectedModel}`);
    }

    if (filters.precisionSearchQuery) {
      parts.push('Precision Mode');
    }
    
    return parts.length > 0 ? parts.join(' • ') : 'No filters active';
  };

  if (isCollapsed) {
    return (
      <div className="bg-white border-r border-gray-200 p-4">
        <button
          onClick={onToggleCollapse}
          className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-700">Filters</span>
            {hasActiveFilters() && (
              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            )}
          </div>
          <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
        
        {hasActiveFilters() && (
          <div className="mt-2 text-xs text-gray-600">
            {getFilterSummary()}
          </div>
        )}
      </div>
    );
  }

  const sections = [
    { id: 'neural', label: 'Neural Search', icon: '🧠' },
    { id: 'model', label: 'AI Model', icon: '🤖' },
    { id: 'date', label: 'Date Range', icon: '📅' },
    { id: 'clients', label: 'Clients', icon: '👥' }
  ];

  return (
    <div className="bg-white border-r border-gray-200 w-80 flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            {hasActiveFilters() && (
              <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">
                Active
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {hasActiveFilters() && (
              <button
                onClick={clearAllFilters}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear All
              </button>
            )}
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
          </div>
        </div>
        
        {hasActiveFilters() && (
          <div className="mt-2 text-sm text-gray-600">
            {getFilterSummary()}
          </div>
        )}
      </div>

      {/* Filter Sections */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* Neural Search Toggle */}
          <div className={`p-4 rounded-lg border ${
            activeSection === 'neural' ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
          }`}>
            <button
              onClick={() => setActiveSection(activeSection === 'neural' ? null : 'neural')}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">🧠</span>
                <span className="font-medium text-gray-900">Neural Search</span>
              </div>
              <div className="flex items-center space-x-2">
                {filters.precisionSearchQuery && (
                  <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                    PRECISION
                  </span>
                )}
                <span className={`text-xs px-2 py-1 rounded-full ${
                  filters.useNeuralSearch 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {filters.useNeuralSearch ? 'ON' : 'OFF'}
                </span>
                <svg 
                  className={`w-4 h-4 text-gray-400 transform transition-transform ${
                    activeSection === 'neural' ? 'rotate-90' : ''
                  }`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
            {activeSection === 'neural' && (
              <div className="mt-4">
                <NeuralSearchToggle filters={filters} onFiltersChange={onFiltersChange} />
              </div>
            )}
          </div>

          {/* Model Selector */}
          <div className={`p-4 rounded-lg border ${
            activeSection === 'model' ? 'border-blue-200 bg-blue-50' : 'border-gray-200'
          }`}>
            <button
              onClick={() => setActiveSection(activeSection === 'model' ? null : 'model')}
              className="w-full flex items-center justify-between text-left"
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg">🤖</span>
                <span className="font-medium text-gray-900">AI Model</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-600">
                  {filters.selectedModel || 'Auto'}
                </span>
                <svg 
                  className={`w-4 h-4 text-gray-400 transform transition-transform ${
                    activeSection === 'model' ? 'rotate-90' : ''
                  }`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
            {activeSection === 'model' && (
              <div className="mt-4">
                <ModelSelector filters={filters} onFiltersChange={onFiltersChange} />
              </div>
            )}
          </div>

          {/* Date Range Filter */}
          <div className={`p-4 rounded-lg border border-gray-200 bg-gray-50 opacity-60 relative`}>
            <div className="absolute top-2 right-2 z-10">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Coming Soon
              </span>
            </div>
            <div className="pointer-events-none">
              <div className="w-full flex items-center justify-between text-left">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">📅</span>
                  <span className="font-medium text-gray-900">Date Range</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600">All Time</span>
                  <svg 
                    className="w-4 h-4 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Client Filter */}
          <div className={`p-4 rounded-lg border border-gray-200 bg-gray-50 opacity-60 relative`}>
            <div className="absolute top-2 right-2 z-10">
              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Coming Soon
              </span>
            </div>
            <div className="pointer-events-none">
              <div className="w-full flex items-center justify-between text-left">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">👥</span>
                  <span className="font-medium text-gray-900">Clients</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-600">0 selected</span>
                  <svg 
                    className="w-4 h-4 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 